/**
 * لاگِ تشخیصیِ روی دستگاه — «این اپ توی چند دقیقه‌ی آخر دقیقاً چه کرد».
 *
 * ═══ چرا، وقتی `devlog` هست ═══
 *
 * `lib/devlog.ts` همین چیزها را ثبت می‌کند ولی فقط برای **یک حساب** (صاحبِ
 * لاگ) و مستقیم روی شبکه می‌فرستد. یعنی وقتی کاربرِ دیگری می‌گوید «کار
 * نکرد»، هیچ‌چیز نداریم جز همان یک جمله. این فایل همان ردیف‌ها را برای
 * **همه** نگه می‌دارد، ولی:
 *
 *   • فقط روی خودِ دستگاه. هیچ سطری خودبه‌خود به شبکه نمی‌رود.
 *   • فقط وقتی به سرور می‌رود که کاربر از «بازخورد» مشکلی را گزارش کند
 *     **و** همان‌جا تیکِ فرستادنش را بزند (نگاه کن به `sendDiag`).
 *   • هر بار که اپ باز می‌شود فایل از نو نوشته می‌شود؛ چیزی روی هم انباشته
 *     نمی‌شود.
 *
 * ═══ کند نکردنِ گوشی — قولی که اندازه گرفته می‌شود ═══
 *
 * ثبتِ هر سطر یک رشته‌سازی و یک `push` است؛ نوشتن روی فایل هر پنج ثانیه
 * یک‌بار و فقط وقتی چیزی عوض شده. ولی «باید ارزان باشد» را حدس نمی‌زنیم:
 * خودِ هزینه اندازه گرفته می‌شود (نوشتن‌ها کامل، ثبتِ سطرها نمونه‌ای) و اگر
 * از بودجه‌ی `diagLine.ts` گذشت، لاگ خودش را برای همیشه‌ی این نشست خاموش
 * می‌کند و همین را در `storage` می‌نویسد تا نشست‌های بعدی هم روی این دستگاه
 * سراغش نروند. هیچ‌کدامِ این‌ها به کاربر گفته نمی‌شود و هیچ کلیدی هم برایش
 * در تنظیمات نیست — این یک تصمیمِ فنی است، نه یک ترجیح.
 *
 * تصمیمِ «کند است» با تعویضِ نسخه‌ی اپ دوباره سنجیده می‌شود: نسخه‌ی بعدی
 * ممکن است سبک‌تر باشد، و یک قضاوتِ همیشگی هرگز اصلاح نمی‌شود.
 *
 * ═══ چه چیزی توی این فایل هست ═══
 *
 * نامِ صفحه‌ها، نامِ دکمه‌ای که زده شده، نامِ کوئری‌ها با مدت و تعدادِ ردیف،
 * و متنِ خطاها. محتوای چیزی که کاربر نوشته یا داده‌ی حسابش تویش نیست. با
 * این حال برچسبِ بعضی دکمه‌ها همان متنی است که روی صفحه دیده می‌شود، پس
 * فایل کاملاً بی‌نام‌ونشان هم نیست — و دقیقاً به همین دلیل بدونِ تیکِ صریحِ
 * کاربر هیچ‌جا نمی‌رود.
 */
import { supabase } from './supabase.ts';
import { isDemoMode } from './demo.ts';
import { trace, traceOn } from './trace.ts';
import { diagFile, platformInfo, storage } from '../platform.ts';
import type { DiagFile } from '../ports.ts';
import { newDevSession, type DevLogRow } from './devlogRow.ts';
import {
  ZERO_COST, diagText, formatDiag, lastSessionOnly, noteCapture, noteWrite, pushLine,
  tooExpensive, type DiagCost,
} from './diagLine.ts';

/** فاصله‌ی بینِ دو بار نوشتن روی فایل. */
const FLUSH_MS = 5_000;
/** هر چند تا ثبت، یکی زمان گرفته می‌شود — نگاه کن به `noteCapture`. */
const SAMPLE_EVERY = 32;
/** جایی که «این دستگاه کند است» یادداشت می‌شود. */
const KEY = 'whosup.diag.v1';

/** ساعتِ یکنواخت، اگر باشد — همان چیزی که `devlog` هم استفاده می‌کند. */
const clock = (): number => {
  const p = (globalThis as { performance?: { now?: () => number } }).performance;
  return p?.now ? p.now() : Date.now();
};

const session = newDevSession();
const startedAt = clock();

/**
 * پیش‌فرض **خاموش** است و فقط `startDiag()` روشنش می‌کند.
 *
 * چرا: هر اپی که یک خط از core را import کند به `dlog` هم می‌رسد (اپِ ادمین
 * هم همین‌طور). اگر این‌جا پیش‌فرض روشن بود، آن اپ‌ها هم برای هیچ‌کس و هیچ‌جا
 * سطر می‌ساختند. روشن شدن باید یک تصمیمِ صریحِ همان اپ باشد.
 */
let on = false;
let file: DiagFile | null = null;
let lines: string[] = [];
let previous = '';
let cost: DiagCost = ZERO_COST;
let seen = 0;
let stopped = '';
let dirty = false;
let timer: ReturnType<typeof setTimeout> | null = null;
let booted = false;
/** خواندنِ فایلِ نشستِ قبلی غیرهمزمان است؛ هرکه `previous` می‌خواهد باید منتظرش بماند. */
let ready: Promise<void> = Promise.resolve();

/** آیا این سطر خریدار دارد — از `devlog.dlog` در داغ‌ترین مسیر صدا زده می‌شود. */
export const diagOn = (): boolean => on;

/* ─────────────────────────── بالا آمدن ─────────────────────────── */

/**
 * روشن کردنِ لاگِ محلی. هر اپ یک بار موقعِ بوت صدایش می‌زند، **بعد از**
 * `initCore` (چون به `storage` و پورتِ فایل نیاز دارد).
 *
 * ترتیبِ کارها این‌جا اهمیت دارد: تا وقتی محتوای قبلیِ فایل خوانده نشده،
 * `file` را برنمی‌داریم — پس هیچ نوشتنی نمی‌تواند وسطِ خواندن، همان چیزی را
 * که داریم می‌خوانیم پاک کند. سطرهایی که در این فاصله ثبت می‌شوند توی
 * حافظه می‌مانند و با اولین نوشتن می‌روند.
 */
export function startDiag(): void {
  if (booted || isDemoMode) return;
  booted = true;
  on = true;
  ready = (async () => {
    try {
      const p = platformInfo();
      const saved = await storage().getItem(KEY);
      if (saved) {
        const { off, v, b } = JSON.parse(saved) as
          { off?: boolean; v?: string | null; b?: string | null };
        /*
         * قضاوتِ نشست‌های قبلی فقط تا وقتی معتبر است که **کد** عوض نشده
         * باشد — و کدِ این پروژه با OTA عوض می‌شود، نه با نسخه‌ی اپ.
         *
         * اول فقط `appVersion` سنجیده می‌شد. آن عدد ماه‌ها ثابت می‌ماند،
         * پس «این دستگاه کند است» عملاً یک قضاوتِ همیشگی شد — همان چیزی
         * که توضیحِ بالای همین فایل می‌گوید نباید بشود. یک گوشیِ واقعی
         * ساعت‌ها لاگِ خاموش داشت و هیچ‌کس نمی‌فهمید چرا هیچ گزارشی نمی‌آید.
         *
         * حالا شناسه‌ی بسته هم شرط است: هر انتشارِ تازه یک فرصتِ دوباره
         * است، چون همان بسته‌ی تازه ممکن است سبک‌تر باشد.
         */
        const sameCode = v === (p.appVersion ?? '?') && (b ?? null) === (p.buildId ?? null);
        if (off && sameCode) { on = false; lines = []; return; }
      }
      const f = diagFile();
      if (!f) return;
      previous = lastSessionOnly(await f.read());
      // همین یک نوشتن است که «هر بار که اپ باز می‌شود فایل از نو نوشته
      // می‌شود» را عملی می‌کند؛ از این به بعد هر نوشتن هم کلِ متن را
      // جایگزین می‌کند، نه اینکه ته‌اش اضافه کند.
      await f.write(diagText(meta(), lines, previous));
      file = f;
      if (dirty) schedule();
    } catch {
      // نه `storage` بود نه فایل — لاگ توی حافظه می‌ماند و همان هم برای
      // گزارشِ همین نشست کافی است.
    }
  })();
  void ready;
}

/**
 * نشستِ قبلی را — اگر مانده باشد — همین حالا بفرست.
 *
 * ═══ چرا این لازم شد ═══
 *
 * اپ بعد از ساختِ یک سیگنال سفید شد و تا بسته‌نشدنِ دستی هیچ کاری نمی‌کرد.
 * هیچ‌کدام از سه ابزارِ موجود چیزی نگرفتند، و هرکدام به دلیلِ خودش:
 *
 *   `client_errors`   خطایی پرتاب نشد. اپ **گیر کرد**، کرش نکرد.
 *   ردپای `trace`     فقط توی حافظه است؛ با بستنِ اپ پرید.
 *   لاگِ فعالیت       دسته‌ای می‌رود؛ سطرهای آخر هیچ‌وقت flush نشدند.
 *
 * ولی همین فایل، همان لحظه، همه‌چیز را داشت — و `startDiag` هم از قبل
 * نشستِ قبلی را می‌خوانَد. تنها چیزی که کم بود این بود که کسی بفرستدش.
 *
 * اپی که گیر کرده نمی‌تواند چیزی بفرستد؛ شبکه‌اش هم ایستاده. تنها راه این
 * است که روی دیسک بماند و **دفعه‌ی بعد** برود. همان کاری که هر گزارشگرِ
 * کرشی می‌کند.
 *
 * ═══ چرا فقط برای صاحبِ لاگ ═══
 *
 * قاعده‌ی بالای همین فایل: هیچ سطری بدونِ تیکِ صریحِ کاربر خودبه‌خود به شبکه
 * نمی‌رود، چون برچسبِ بعضی دکمه‌ها همان متنِ روی صفحه است و فایل کاملاً
 * بی‌نام‌ونشان نیست. آن قاعده سرِ جایش می‌مانَد؛ این استثنا فقط برای حسابی
 * است که ردپا را روشن کرده و خودش همین حالا هم لاگش را می‌فرستد.
 *
 * برمی‌گرداند: شناسه‌ی نشست اگر فرستاده شد، وگرنه `null`.
 */
export async function sendPreviousDiag(): Promise<string | null> {
  if (!traceOn()) return null;
  await ready;
  /*
   * چرا هر شاخه یک سطرِ ردپا می‌نویسد: یک شب کامل معلوم نبود چرا هیچ لاگی
   * نمی‌آید. سه دلیلِ ممکن داشت و از بیرون از هم قابلِ تشخیص نبودند —
   * ترجیح خاموش، لاگِ محلیِ خاموش‌شده به خاطرِ کندیِ دستگاه، یا نبودِ
   * نشستِ قبلی. ابزارِ تشخیصی که خودش ساکت شکست بخورد، بدتر از نداشتنش
   * است.
   *
   * از `trace` می‌رود نه `dlog`: آن یکی از همین فایل می‌خوانَد و ایمپورتِ
   * حلقوی می‌شود.
   */
  if (!on) {
    trace.log(`diag: خاموش${stopped ? ` (${stopped})` : ''}`, { soon: true });
    return null;
  }
  if (!previous.trim()) {
    trace.log('diag: نشستِ قبلی خالی بود', { soon: true });
    return null;
  }
  const id = await sendDiag('autostart');
  trace.log(`diag: ${id ? `رفت (${previous.length} کاراکتر)` : 'نرفت'}`, { soon: true });
  return id;
}

/* ─────────────────────────── ثبت ─────────────────────────── */

/**
 * یک ردیفِ `devlog` را این‌جا هم بنشان.
 *
 * تنها صداکننده‌اش `dlog` است — یعنی هر کوئری، هر تپ، هر ناوبری و هر خطا
 * بدونِ اینکه هیچ صفحه‌ای کاری بکند از این‌جا رد می‌شود.
 */
export function captureDiag(row: DevLogRow): void {
  if (!on) return;
  seen += 1;
  const sample = seen % SAMPLE_EVERY === 0;
  const t0 = sample ? clock() : 0;
  try {
    pushLine(lines, formatDiag(row, clock() - startedAt));
    dirty = true;
    schedule();
  } catch {
    // لاگ کردن هیچ‌وقت نباید مسیرِ کاربر را بشکند.
  }
  if (sample) {
    cost = noteCapture(cost, clock() - t0, SAMPLE_EVERY);
    // `remember` نه: این عدد یک تخمین است (نگاه کن به `noteCapture`) و
    // تخمین حق ندارد یک دستگاه را برای همیشه از لاگ محروم کند.
    if (tooExpensive(cost)) stop('capture', false);
  }
}

function schedule(): void {
  if (timer || !file || !on) return;
  timer = setTimeout(() => { timer = null; void write(); }, FLUSH_MS);
}

/**
 * هرچه در حافظه هست همین حالا روی فایل — برای رفتنِ اپ به پس‌زمینه یا پنهان
 * شدنِ تب. بدونِ این، آخرین سطرهای هر نشست (که معمولاً همان‌هایی‌اند که
 * دنبالشان می‌گردی) فقط توی حافظه‌ای می‌مانند که دارد بسته می‌شود.
 */
export function flushDiag(): void {
  if (timer) { clearTimeout(timer); timer = null; }
  void write();
}

async function write(): Promise<void> {
  if (!file || !on || !dirty) return;
  dirty = false;
  const t0 = clock();
  try {
    await file.write(diagText(meta(), lines, previous));
  } catch {
    // فایل در دسترس نیست (فضا تمام شده، حافظه‌ی مرورگر بسته است). دوباره
    // تلاش نمی‌کنیم: همان خطا هر پنج ثانیه تکرار می‌شد و هزینه‌اش را هم
    // کاربر می‌داد. لاگ توی حافظه به کارش ادامه می‌دهد.
    file = null;
    return;
  }
  cost = noteWrite(cost, clock() - t0);
  if (tooExpensive(cost)) stop('write', true);
}

/**
 * خاموش کردنِ لاگ تا آخرِ این نشست.
 *
 * سطرهایی که تا حالا جمع شده‌اند **نگه داشته می‌شوند**: هزینه‌ی این ویژگی
 * جمع کردن و نوشتن است، نه نگه داشتن. اگر کاربرِ همین گوشیِ کند همین حالا
 * مشکلی را گزارش کند، بهتر است چیزی داشته باشیم تا هیچ.
 *
 * `remember` یعنی نشست‌های بعدی هم اصلاً شروع نکنند. فقط وقتی درست است که
 * قضاوت روی یک زمانِ **اندازه‌گیری‌شده‌ی** واقعی بنا شده باشد — یعنی مسیرِ
 * نوشتن.
 */
function stop(reason: string, remember: boolean): void {
  if (!on) return;
  on = false;
  stopped = reason;
  if (timer) { clearTimeout(timer); timer = null; }
  dirty = false;
  if (!remember) return;
  try {
    const p = platformInfo();
    void storage().setItem(KEY, JSON.stringify({
      off: true, v: p.appVersion ?? '?', b: p.buildId ?? null,
    }));
  } catch {
    // نشد هم نشد؛ نشستِ بعدی دوباره می‌سنجد.
  }
}

/* ─────────────────────────── فرستادن ─────────────────────────── */

function meta() {
  let appVersion: string | null = null;
  let platform = '?';
  try {
    const p = platformInfo();
    appVersion = p.appVersion;
    platform = `${p.os} ${p.osVersion}`;
  } catch {
    // پیش از initCore — عملاً پیش نمی‌آید، ولی سرآیند نباید دلیلِ نرفتنِ لاگ شود.
  }
  return { session, appVersion, platform, at: new Date().toISOString(), seen, stopped };
}

/** متنی که به کاربر نشان داده نمی‌شود ولی همانی است که فرستاده می‌شود. */
export const diagReport = (): string => diagText(meta(), lines, previous);

/**
 * فرستادنِ گزارش — تنها راهِ رسیدنِ این لاگ به شبکه.
 *
 * به همان جدولی می‌رود که خطاها و خودِ بازخورد می‌روند (`client_errors`)، با
 * `where_at = 'diag'`، و شناسه‌ی نشست را برمی‌گرداند تا صفحه‌ی بازخورد همان
 * را به ردیفِ خودش وصل کند. دو ردیف، یک شناسه:
 *
 *   select * from client_errors where detail->>'session' = '<id>'
 *      or detail->>'diag' = '<id>' order by created_at;
 *
 * `getSession` و نه `getUser`: اولی نشستِ ذخیره‌شده‌ی همین دستگاه را
 * می‌خواند و دومی یک رفت‌وبرگشتِ شبکه است. سیاستِ `ce_ins` ردیفِ بی‌صاحب را
 * هم قبول می‌کند، ولی وقتی شناسه در دسترس است می‌گذاریمش — تا پاک کردنِ
 * حساب (مایگریشن 0046) این ردیف را هم با خودش ببرد.
 */
export async function sendDiag(where: string): Promise<string | null> {
  if (isDemoMode) return null;
  try {
    const text = diagReport();
    const p = platformInfo();
    const { data } = await supabase.auth.getSession();
    const { error } = await supabase.from('client_errors').insert({
      user_id: data.session?.user.id ?? null,
      where_at: 'diag',
      message: `${where} · ${session}`,
      detail: { session, lines: text, seen, stopped: stopped || undefined },
      app_version: p.appVersion,
      platform: `${p.os} ${p.osVersion}`,
    });
    return error ? null : session;
  } catch {
    return null;
  }
}

/** فقط برای تست‌ها: برگرداندن همه‌چیز به حالتِ اول. */
export function resetDiagForTest(): void {
  on = false; booted = false; file = null; lines = []; previous = '';
  cost = ZERO_COST; seen = 0; stopped = ''; dirty = false;
  if (timer) { clearTimeout(timer); timer = null; }
}

/** فقط برای تست‌ها. */
export const diagLinesForTest = (): string[] => lines;
